import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, message, Space, Alert, Spin } from 'antd';
import { MailOutlined, LockOutlined, UserOutlined, BankOutlined, TeamOutlined } from '@ant-design/icons';
import useAuthStore from '../../store/authStore';
import organizationService from '../../services/organizationService';

const { Title, Text } = Typography;

const RegisterPage = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuthStore();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);

  useEffect(() => {
    if (inviteToken) {
      organizationService.getInviteInfo(inviteToken)
        .then((res) => setInviteInfo(res.data))
        .catch(() => message.warning('Could not load invitation details. You can still register.'))
        .finally(() => setInviteLoading(false));
    }
  }, [inviteToken]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await register({
        email: values.email,
        password: values.password,
        fullName: values.fullName,
        organizationName: values.organizationName,
      });

      // If registering via invite, auto-accept the invitation
      if (inviteToken) {
        try {
          const res = await organizationService.acceptInvite(inviteToken);
          localStorage.setItem('organizationId', res.data.organizationId);
          message.success(`Welcome! You've joined ${res.data.organizationName}.`);
          navigate('/');
          window.location.reload();
          return;
        } catch {
          message.info('Registration successful! You can accept the invitation from the link in your email.');
        }
      }

      message.success('Registration successful! Welcome to MkraftOne.');
      navigate('/');
    } catch (error) {
      message.error(error?.error?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (inviteLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1B3A5C 0%, #2a5580 50%, #1B3A5C 100%)' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1B3A5C 0%, #2a5580 50%, #1B3A5C 100%)',
      padding: 24,
    }}>
      <Card style={{ width: 460, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={2} style={{ margin: 0, color: '#1B3A5C' }}>MkraftOne</Title>
            <Text type="secondary">
              {inviteInfo ? 'Register to join an organization' : 'Create your workspace'}
            </Text>
          </div>

          {inviteInfo && (
            <Alert
              type="info"
              showIcon
              icon={<TeamOutlined />}
              message={`You're invited to join ${inviteInfo.organizationName}`}
              description={`${inviteInfo.invitedByName} invited you as ${inviteInfo.roleName}. Register below to accept.`}
            />
          )}

          <Form
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            requiredMark={false}
            initialValues={inviteInfo ? { email: inviteInfo.email } : undefined}
          >
            <Form.Item
              name="fullName"
              rules={[
                { required: true, message: 'Please enter your full name' },
                { min: 2, message: 'Name must be at least 2 characters' },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Full name"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="Work email"
                size="large"
                disabled={!!inviteInfo?.email}
              />
            </Form.Item>

            {!inviteToken && (
              <Form.Item
                name="organizationName"
                rules={[
                  { required: true, message: 'Please enter your organization name' },
                  { min: 2, message: 'Organization name must be at least 2 characters' },
                ]}
              >
                <Input
                  prefix={<BankOutlined />}
                  placeholder="Organization name"
                  size="large"
                />
              </Form.Item>
            )}

            {inviteToken && (
              <Form.Item
                name="organizationName"
                initialValue="Personal"
                hidden
              >
                <Input />
              </Form.Item>
            )}

            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please enter a password' },
                { min: 8, message: 'Password must be at least 8 characters' },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: 'Must contain uppercase, lowercase, and a number',
                },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm password"
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                {inviteToken ? 'Register & Join' : 'Create Account'}
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Text>
              Already have an account?{' '}
              <Link to={inviteToken ? `/login?redirect=/invite/${inviteToken}` : '/login'}>Sign in</Link>
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default RegisterPage;
